import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatContext } from '@fytness/shared';
import { RightSidebar } from '~/layout/components/right-sidebar/RightSidebar';
import { RightSidebarTab } from '~/layout/contexts/right-sidebar-layout/RightSidebarLayout';
import { SidebarChat } from '~/chat/components/sidebar-chat/SidebarChat';
import { WorkoutFavouritesPanel } from '~/workout/components/workout-favourites-panel/WorkoutFavouritesPanel';

export interface WorkoutBuilderSidebarProps {
  /** The open workout (UUID); `null` in create mode. Scopes the chat + adds. */
  workoutId: string | null;
}

/**
 * Right-sidebar rail for the workout builder: an AI coach chat scoped to the
 * open workout, plus the user's favourite (most-used) exercises. Renders nothing
 * itself — `<RightSidebar>` registers the tabs. The coach chat is available to
 * everyone (Free users run it against their monthly FYT credits; an empty wallet
 * is handled at send time).
 */
export const WorkoutBuilderSidebar = ({
  workoutId,
}: WorkoutBuilderSidebarProps) => {
  const { t } = useTranslation('workout');

  const tabs = useMemo<RightSidebarTab[]>(
    () => [
      {
        key: 'coach',
        icon: 'Bot',
        label: t('rightSidebar.tabs.coach'),
        panel: (
          <SidebarChat
            context={ChatContext.WORKOUT}
            contextRefId={workoutId}
            title={t('rightSidebar.tabs.coach')}
            placeholder={t('rightSidebar.chat.placeholder')}
          />
        ),
      },
      {
        key: 'favourites',
        icon: 'Heart',
        label: t('rightSidebar.tabs.favourites'),
        panel: <WorkoutFavouritesPanel workoutId={workoutId} />,
      },
    ],
    [t, workoutId],
  );

  return <RightSidebar tabs={tabs} expandedWidth={340} />;
};
